import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { Box3, MathUtils, PerspectiveCamera, Vector3 } from 'three';

interface CenteredCameraProps {
  boundingBox: Box3;
}

const ELEVATION = MathUtils.degToRad(55);
const MARGIN = 1.1;

/** Distance at which every corner of `box` (centred on the origin) fits the view. */
function fitDistance(
  box: Box3,
  vFov: number,
  aspect: number,
  direction: Vector3
): number {
  const size = box.getSize(new Vector3());
  const tanV = Math.tan(vFov / 2);
  const tanH = tanV * aspect;
  const up = new Vector3(0, 0, 1)
    .sub(direction.clone().multiplyScalar(direction.z))
    .normalize();
  const right = new Vector3().crossVectors(up, direction).normalize();

  let distance = 0;
  [-0.5, 0.5].forEach((sx) => {
    [-0.5, 0.5].forEach((sy) => {
      [-0.5, 0.5].forEach((sz) => {
        const corner = new Vector3(sx * size.x, sy * size.y, sz * size.z);
        const along = corner.dot(direction);
        distance = Math.max(
          distance,
          along + Math.abs(corner.dot(up)) / tanV,
          along + Math.abs(corner.dot(right)) / tanH
        );
      });
    });
  });
  return Math.max(distance, 1) * MARGIN;
}

function CenteredCamera({ boundingBox }: CenteredCameraProps) {
  const { camera, size } = useThree();

  useEffect(() => {
    if (!boundingBox) return;
    const perspective = camera as PerspectiveCamera;
    const direction = new Vector3(0, -Math.cos(ELEVATION), Math.sin(ELEVATION));
    const distance = fitDistance(
      boundingBox,
      MathUtils.degToRad(perspective.fov),
      size.width / size.height,
      direction
    );

    camera.position.copy(direction.multiplyScalar(distance));
    camera.up.set(0, 0, 1);
    camera.lookAt(0, 0, 0);
    // r3f's default far plane (2000) clips parts over ~1 m.
    camera.near = distance / 1000;
    camera.far = distance * 10;
    camera.updateProjectionMatrix();
  }, [camera, boundingBox, size.width, size.height]);

  return null;
}

export default CenteredCamera;
